import React, { useCallback, useMemo, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { Close, Download } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { FileWithPath, useDropzone } from 'react-dropzone';
import { GlobalContext } from '../../../providers/GlobalProvider';
import { useCreateBulkEntityRelationships } from '../../../api/bitedb/entityRelationships/entityRelationships.hooks';
import { getAllEntitiesByDatabaseId } from '../../../api/bitedb/entities/entities.service';
import {
  downloadRelationshipCSVTemplate,
  ENTITY_RELATIONSHIP_TYPES,
  formatEntityRelationshipTypeLabel,
  ParsedRelationshipRow,
  parseRelationshipCSV,
  RELATIONSHIP_BULK_UPLOAD_MAX_ROWS,
  RELATIONSHIP_SOURCE_BUSINESS_HEADER,
  RELATIONSHIP_SOURCE_LINK_HEADER,
  RELATIONSHIP_SOURCE_NAME_HEADER,
  RELATIONSHIP_SOURCE_PERSON_HEADER,
  RELATIONSHIP_TARGET_BUSINESS_HEADER,
  RELATIONSHIP_TARGET_LINK_HEADER,
  RELATIONSHIP_TARGET_NAME_HEADER,
  RELATIONSHIP_TARGET_PERSON_HEADER,
  RELATIONSHIP_TYPE_HEADER,
} from '../../../utils/biteDbRelationshipTemplates';

const chipStyle: React.CSSProperties = {
  padding: '4px 8px',
  borderRadius: '999px',
  border: '1px solid var(--secondary-color)',
  background: 'var(--input-color)',
  color: 'var(--text-primary)',
  fontSize: '11px',
  fontWeight: 500,
  lineHeight: 1.2,
};

interface BulkLinkEntitiesModalProps {
  open: boolean;
  onClose: () => void;
  databaseId: string;
  onSuccess?: () => void;
}

const BulkLinkEntitiesModal: React.FC<BulkLinkEntitiesModalProps> = ({
  open,
  onClose,
  databaseId,
  onSuccess,
}) => {
  const { cur_user } = React.useContext<any>(GlobalContext);
  const [parsedRelationships, setParsedRelationships] = useState<
    ParsedRelationshipRow[]
  >([]);
  const [isUploading, setIsUploading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [parsedCount, setParsedCount] = useState(0);
  const [isParsing, setIsParsing] = useState(false);
  const [isDownloadingEntityReference, setIsDownloadingEntityReference] =
    useState(false);

  const bulkRelationshipMutation = useCreateBulkEntityRelationships();

  const handleDownloadTemplate = useCallback(() => {
    downloadRelationshipCSVTemplate('relationship_template.csv');
  }, []);

  const handleDownloadEntityReference = useCallback(async () => {
    setIsDownloadingEntityReference(true);
    try {
      const entities = await getAllEntitiesByDatabaseId(databaseId);
      if (entities.length === 0) {
        enqueueSnackbar('No entities found in this database.', { variant: 'info' });
        return;
      }

      const headers = ['link_id', 'name', 'country_of_origin', 'created_at'];
      const csvRows = [headers.join(',')];
      entities.forEach((entity) => {
        const values = [
          entity.link_id || '',
          entity.name || '',
          entity.country_of_origin || '',
          entity.created_at || '',
        ].map((value) => `"${String(value).replace(/"/g, '""')}"`);
        csvRows.push(values.join(','));
      });

      const blob = new Blob([csvRows.join('\r\n')], {
        type: 'text/csv;charset=utf-8;',
      });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'entity_link_reference.csv';
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error: any) {
      enqueueSnackbar(
        error?.message || 'Failed to download entity link reference CSV',
        { variant: 'error' },
      );
    } finally {
      setIsDownloadingEntityReference(false);
    }
  }, [databaseId]);

  const handleFileParsed = useCallback(
    async (selectedFile: File) => {
      setIsParsing(true);
      setParseErrors([]);
      setParsedCount(0);
      try {
        const { rows, errors } = await parseRelationshipCSV(selectedFile);
        setParseErrors(errors);
        setParsedCount(rows.length);
        setParsedRelationships(rows);
      } catch (err: any) {
        setParseErrors([err.message || 'Failed to parse file']);
        setParsedRelationships([]);
      } finally {
        setIsParsing(false);
      }
    },
    [],
  );

  const onDrop = useCallback(
    async (acceptedFiles: FileWithPath[]) => {
      const selectedFile = acceptedFiles[0];
      if (!selectedFile) return;
      setFile(selectedFile as unknown as File);
      await handleFileParsed(selectedFile as unknown as File);
    },
    [handleFileParsed],
  );

  const handleRemoveFile = useCallback(() => {
    setFile(null);
    setParseErrors([]);
    setParsedCount(0);
    setParsedRelationships([]);
  }, []);

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } =
    useDropzone({
      onDrop: onDrop,
      accept: ['.csv', '.xlsx'],
      multiple: false,
    });

  const dropzoneClassName = useMemo(() => {
    const baseClass = 'dropzone-base';
    if (isDragAccept) return `${baseClass} dropzone-accept`;
    if (isDragReject) return `${baseClass} dropzone-reject`;
    if (isDragActive) return `${baseClass} dropzone-active`;
    return file ? `${baseClass} dropzone-file-present` : `${baseClass} dropzone-default`;
  }, [isDragActive, isDragAccept, isDragReject, file]);

  const handleSubmit = useCallback(async () => {
    if (parsedRelationships.length === 0) {
      enqueueSnackbar('No relationships to upload.', { variant: 'warning' });
      return;
    }

    setIsUploading(true);
    try {
      const result = await bulkRelationshipMutation.mutateAsync({
        payload: {
          relationships: parsedRelationships,
          database_id: databaseId,
        },
        teamId: cur_user.selected_team?.id,
      });

      if (result?.success) {
        const inserted = result.data?.inserted?.length || 0;
        const failed = result.data?.failed?.length || 0;
        const unresolved = result.data?.unresolved?.length || 0;
        let msg = `${inserted} relationship${inserted !== 1 ? 's' : ''} created`;
        if (failed > 0) msg += `, ${failed} failed`;
        if (unresolved > 0) msg += `, ${unresolved} unresolved`;
        enqueueSnackbar(msg, {
          variant: inserted > 0 ? 'success' : 'warning',
        });
        onSuccess?.();
        onClose();
      } else {
        enqueueSnackbar(
          result?.message || 'Failed to create relationships',
          { variant: 'error' },
        );
      }
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to upload relationships', {
        variant: 'error',
      });
    } finally {
      setIsUploading(false);
    }
  }, [
    parsedRelationships,
    databaseId,
    bulkRelationshipMutation,
    cur_user.selected_team?.id,
    onSuccess,
    onClose,
  ]);

  if (!open) return null;

  return (
    <div className='login-wizard-container login-wizard-container-wide'>
      <button
        onClick={onClose}
        className='modal-close-button'
        onMouseEnter={(e) => {
          e.currentTarget.style.color = '#333';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = '#666';
        }}
      >
        <Close fontSize='medium' />
      </button>

      <div className='welcome-header'>
        <div className='welcome-header-text'>Link Entities</div>
      </div>

      <div
        className='team-create-container wizard-step-content'
        style={{ minHeight: '420px', height: 'auto', 
                  overflowY: 'auto' }}
      >
        <div className='wizard-team-graphic-container'>
          <div className='audit-add-modal-cont-body-frame-form'>
            <div className='audit-add-modal-head-cont-supporting'>
              <h1>Upload a relationship file to link entities</h1>
            </div>
            <div className='modal-grid'>
              <div className='modal-section'>
                <h3 className='modal-section-title'>1. Download Template</h3>
                <button
                  onClick={handleDownloadTemplate}
                  className='button button-primary button-full-width'
                  type='button'
                >
                  <Download style={{ marginRight: '8px' }} />
                  Download CSV Template
                </button>
                <p className='modal-section-subtitle'>
                  Download a .csv template with the required columns for
                  defining entity relationships. Upload up to{' '}
                  {RELATIONSHIP_BULK_UPLOAD_MAX_ROWS.toLocaleString()}{' '}
                  relationships per file.
                </p>
                <button
                  onClick={handleDownloadEntityReference}
                  className='button button-secondary button-full-width'
                  type='button'
                  style={{ marginTop: '12px' }}
                  disabled={isDownloadingEntityReference}
                >
                  <Download style={{ marginRight: '8px' }} />
                  {isDownloadingEntityReference
                    ? 'Preparing Entity CSV...'
                    : 'Download Entity Link CSV'}
                </button>
                <p className='modal-section-subtitle'>
                  Download all entities in this database with their{' '}
                  <code>link_id</code>, name, entity country, and created
                  date so you can build relationship files from a stable
                  system-generated identifier.
                </p>
              </div>
              <div className='modal-section'>
                <h3 className='modal-section-title'>2. Upload File</h3>
                <div {...getRootProps()} className={dropzoneClassName}>
                  <input {...getInputProps()} />
                  {file ? (
                    <div className='dropzone-content-item'>
                      {isParsing ? (
                        <CircularProgress
                          size={20}
                          style={{ color: 'var(--text-primary)' }}
                        />
                      ) : (
                        <svg
                          className='dropzone-icon-success'
                          xmlns='http://www.w3.org/2000/svg'
                          fill='none'
                          viewBox='0 0 24 24'
                          strokeWidth={1.5}
                          stroke='currentColor'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            d='M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z'
                          />
                        </svg>
                      )}
                      <p className='dropzone-filename'>{file.name}</p>
                      <p className='dropzone-filesize'>
                        {parsedCount > 0
                          ? `${parsedCount} relationships found`
                          : 'No valid relationships yet'}
                      </p>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFile();
                        }}
                        className='dropzone-remove-button'
                      >
                        Remove File
                      </button>
                    </div>
                  ) : (
                    <div className='dropzone-content-item'>
                      <p className='dropzone-text'>
                        <span className='dropzone-text-highlight'>
                          Click to upload
                        </span>{' '}
                        or drag and drop
                      </p>
                      <p className='dropzone-subtext'>
                        CSV or XLSX, up to{' '}
                        {RELATIONSHIP_BULK_UPLOAD_MAX_ROWS.toLocaleString()}{' '}
                        rows
                      </p>
                      {isDragReject && (
                        <p className='dropzone-error-text'>
                          Invalid file type or size.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {parseErrors.length > 0 && (
              <div
                style={{
                  marginTop: '8px',
                  width: '100%',
                  padding: '10px 12px',
                  boxSizing: 'border-box',
                  borderRadius: '8px',
                  border: '1px solid #ef4444',
                  background: 'rgba(239, 68, 68, 0.08)',
                  fontSize: '12px',
                  color: '#ef4444',
                  maxHeight: '120px',
                  overflowY: 'auto',
                }}
              >
                {parseErrors.map((err, i) => (
                  <div key={i}>{err}</div>
                ))}
              </div>
            )}

            <div
              style={{
                marginTop: '12px',
                padding: '12px',
                borderRadius: '10px',
                border: '1px solid var(--secondary-color)',
                background: 'var(--input-color)',
              }}
            >
              <div
                style={{
                  color: 'var(--text-primary)',
                  fontSize: '13px',
                  fontWeight: 600,
                  marginBottom: '6px',
                }}
              >
                Valid relationship types
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {ENTITY_RELATIONSHIP_TYPES.map((type) => (
                  <span key={type} style={chipStyle}>
                    {formatEntityRelationshipTypeLabel(type)}
                  </span>
                ))}
              </div>
            </div>

            <p className='modal-section-subtitle' style={{ marginTop: '8px' }}>
              Use the columns <code>{RELATIONSHIP_SOURCE_LINK_HEADER}</code>,{' '}
              <code>{RELATIONSHIP_SOURCE_BUSINESS_HEADER}</code>, or{' '}
              <code>{RELATIONSHIP_SOURCE_PERSON_HEADER}</code> to identify the
              starting entity (source), and{' '}
              <code>{RELATIONSHIP_TARGET_LINK_HEADER}</code>,{' '}
              <code>{RELATIONSHIP_TARGET_BUSINESS_HEADER}</code>, or{' '}
              <code>{RELATIONSHIP_TARGET_PERSON_HEADER}</code> to identify the
              related entity (target) - enter only one identifier for each. Use{' '}
              <code>{RELATIONSHIP_SOURCE_NAME_HEADER}</code> and{' '}
              <code>{RELATIONSHIP_TARGET_NAME_HEADER}</code> as optional tracker
              columns for your own reference; they are ignored during upload and
              are not sent to the backend. Each row should read as &quot;Source{' '}
              {'→'} Target,&quot; and the <code>{RELATIONSHIP_TYPE_HEADER}</code>
              describes how the target relates to the source (e.g., company{' '}
              {'→'} person for <code>ceo</code>, parent company {'→'} subsidiary
              for <code>subsidiary</code>, or child company {'→'} parent company
              for{' '}
              <code>parent_company</code>). You may use either
              system-generated <code>link_id</code>s or your existing business
              or person identifiers. Upload up to{' '}
              {RELATIONSHIP_BULK_UPLOAD_MAX_ROWS.toLocaleString()} relationships
              per file.
            </p>
          </div>
        </div>
      </div>

      <div className='wizard-btn-group'>
        <div className='wizard-btn-container'>
          <div className='wizard-btn-dropdown'>
            <div className='wizard-back-btn' onClick={onClose}>
              <div className='wizard-btn-text-padding'>
                <div className='wizard-back-btn-text'>Cancel</div>
              </div>
            </div>
          </div>
          <div
            className='wizard-primary-btn'
            onClick={handleSubmit}
            style={{
              opacity:
                parsedRelationships.length === 0 || isUploading ? 0.5 : 1,
              pointerEvents:
                parsedRelationships.length === 0 || isUploading
                  ? 'none'
                  : 'auto',
            }}
          >
            <div className='wizard-next-btn-text'>
              {isUploading
                ? 'Uploading...'
                : parsedRelationships.length > 0
                ? `Upload ${parsedRelationships.length} Relationship${
                    parsedRelationships.length !== 1 ? 's' : ''
                  }`
                : 'Upload Relationships'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkLinkEntitiesModal;

